import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Button, Icon } from "../kit";
import { RETAIL_MARGIN, estimateRingPrice, formatUSD, meleeQualityFactor } from "./SpecPanel";
import type { Catalogs, OptionItem, RingState } from "../../shared/api-contract";

/**
 * The printable Specification Sheet — the document a jeweller hands a client or
 * a bench, and the reference app's `ringSpecRows()` modal rebuilt on the merged
 * v37 `RingState`.
 *
 * Every figure is read straight off the live `RingState` / `RingSpec`; nothing
 * is recomputed here. A row whose figure the engine does not report is omitted
 * rather than guessed — the one exception is the UK size letter, which is a
 * pure unit conversion of the EU circumference the engine *does* report (see
 * `ukLetter`).
 *
 * Print/PDF is `window.print()` over an injected `@media print` sheet: the app
 * chrome is hidden and the document renders black-on-white. The modal is
 * portalled to `document.body` so "hide everything that is not the sheet" is a
 * single `body > *` rule.
 */

// ─── Formatting helpers ─────────────────────────────────────────────────────

/** "hidden-halo" → "Hidden Halo". Covers every kebab enum in the contract
 * without duplicating the tabs' label tables. */
function titleCase(id: string): string {
  return id.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function labelOf(list: readonly OptionItem[] | undefined, id: string): string {
  return list?.find((o) => o.id === id)?.label ?? titleCase(id);
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * UK ring size letter from the EU size. EU size IS the inner circumference in
 * mm (`RING_SIZES`: EU 44 ↔ Ø 14.0 mm), and the UK scale steps one letter per
 * ~1.25 mm of circumference from A at 37.8 mm — the reference app's conversion,
 * kept verbatim so the two sheets agree.
 *
 * Whole US sizes sit on UK HALF sizes, so this is the NEAREST letter, not an
 * exact equivalent (checked against a standard chart over the engine's whole
 * `RING_SIZES` table: 9/11 exact, US 9 → S and US 11 → W round up off R½/V½).
 * The sheet says so in its footer.
 */
function ukLetter(eu: number): string {
  const i = Math.round((eu - 37.8) / 1.25);
  return LETTERS[Math.max(0, Math.min(LETTERS.length - 1, i))];
}

// ─── Row model ──────────────────────────────────────────────────────────────

type Row = [label: string, value: string];
interface Block {
  title: string;
  rows: Row[];
}

/** Drops rows whose figure is missing, so nothing prints as "—" or invented. */
function block(title: string, rows: (Row | null)[]): Block | null {
  const kept = rows.filter((r): r is Row => r !== null);
  return kept.length ? { title, rows: kept } : null;
}

function blocksFor(state: RingState, catalogs: Catalogs): Block[] {
  const spec = state.spec;
  const d = state.diamond;
  const g = state.grooves;
  const ch = state.channel;
  const e = state.engagement?.enabled ? state.engagement : null;
  const price = estimateRingPrice(state);

  const band = block("Band", [
    ["Profile", labelOf(catalogs.profiles, state.profileId)],
    state.shape ? ["Silhouette", labelOf(catalogs.ringShapes, state.shape)] : null,
    ["Ring size", `US ${state.ringSizeUS} · EU ${state.ringSizeEU} · UK ${ukLetter(state.ringSizeEU)}`],
    spec ? ["Inner Ø", `${spec.innerDiameterMM.toFixed(2)} mm`] : null,
    spec ? ["Circumference", `${spec.circumferenceMM.toFixed(1)} mm`] : null,
    ["Width × thickness", `${state.widthMM.toFixed(2)} × ${state.thicknessMM.toFixed(2)} mm`],
    ["Edge", labelOf(catalogs.edgeStyles, state.edgeStyle)],
    state.comfortFitMM ? ["Comfort fit", `${state.comfortFitMM.toFixed(2)} mm`] : null,
  ]);

  const partitions: Row[] = state.materials.map((m, i) => [
    state.materials.length > 1 ? `Metal ${i + 1}` : "Metal",
    `${labelOf(catalogs.metalPresets, m.presetId)} · ${labelOf(catalogs.metalFinishes, m.finishId)}`,
  ]);

  const metal = block("Metal", [
    ...partitions,
    ["Karat", labelOf(catalogs.metalKarats, state.metalKarat)],
    state.splitMode !== "none"
      ? ["Split pattern", labelOf(catalogs.splitPatterns, state.splitPatternId)]
      : null,
    state.division && state.materials.length > 1
      ? ["Division", labelOf(catalogs.divisionStyles, state.division)]
      : null,
  ]);

  // The melee-run descriptors only mean something when a run is actually set:
  // with `style: "none"` the engine still reports a stone count (the engagement
  // centre stone), and printing "Round · 1.50 mm" beside it would invent a run
  // the bench would then try to cut.
  const melee = d.style !== "none";
  // Gold beads are metal, so the diamond specification below has nothing to
  // describe when the run is beads.
  const gems = melee && d.type !== "goldBead";
  const stones = block("Stones", [
    melee ? ["Setting", labelOf(catalogs.diamondStyles, d.style)] : null,
    melee ? ["Stone shape", labelOf(catalogs.stoneShapes, d.stoneShape)] : null,
    melee ? ["Stone size", `${d.stoneSizeMM.toFixed(2)} mm`] : null,
    spec && spec.stoneCount > 0 ? ["Count", String(spec.stoneCount)] : null,
    spec && spec.perStoneCarat > 0 ? ["Per stone", `${spec.perStoneCarat.toFixed(3)} ct`] : null,
    spec && spec.totalCarat > 0 ? ["Total carat", `${spec.totalCarat.toFixed(3)} ct`] : null,
    // v43 specification (the free editor's Custom Settings) — the band run, not
    // the engagement centre stone, which has its own origin row below. Colour and
    // clarity print only when chosen; a sheet must not invent a grade.
    gems ? ["Diamond origin", labelOf(catalogs.diamondOrigins, d.origin ?? "natural")] : null,
    gems && d.colorGrade ? ["Colour quality (GIA)", d.colorGrade] : null,
    gems && d.clarity ? ["Clarity", labelOf(catalogs.gemClarities, d.clarity)] : null,
  ]);

  const joints = block("Joints & Grooves", [
    g.pattern !== "none" ? ["Grooves", labelOf(catalogs.groovePatterns, g.pattern)] : null,
    g.pattern !== "none" || g.jointType
      ? ["Groove size", `${g.widthMM.toFixed(2)} × ${g.depthMM.toFixed(2)} mm`]
      : null,
    g.jointType ? ["Cross-section", labelOf(catalogs.jointTypes, g.jointType)] : null,
    g.seam ? ["Seam grooves", "Yes"] : null,
    g.edges ? ["Edge grooves", "Yes"] : null,
    ch.enabled ? ["Channel cut", `${titleCase(ch.cutShape)}${ch.fullThrough ? " · full through" : ""}`] : null,
    ch.enabled
      ? [
          "Channel size",
          `W ${ch.widthMM.toFixed(2)} × H ${ch.heightMM.toFixed(2)} × D ${ch.depthMM.toFixed(2)} mm`,
        ]
      : null,
  ]);

  const engraving = state.engravingText
    ? block("Engraving", [
        ["Text", `“${state.engravingText}”`],
        ["Font", state.engraving.fontFamily],
      ])
    : null;

  const halo = ["halo", "hidden-halo", "double-halo"].includes(e?.head.understructure ?? "");
  const mm = spec?.centerStoneMM;
  const engagement = e
    ? block("Engagement head", [
        ["Centre stone", `${titleCase(e.centerStone.shape)} · ${e.centerStone.caratCt.toFixed(2)} ct`],
        ["Diamond origin", titleCase(e.centerStone.origin)],
        mm ? ["Measurements", `${mm.l.toFixed(2)} × ${mm.w.toFixed(2)} × ${mm.d.toFixed(2)} mm`] : null,
        ["Understructure", titleCase(e.head.understructure)],
        ["Prongs", `${titleCase(e.head.prongCount)} · ${titleCase(e.head.prongTip)} tip`],
        e.head.prongPave ? ["Prong pavé", "Yes"] : null,
        halo ? ["Halo stone", `${e.head.haloStoneSizeMM.toFixed(2)} mm`] : null,
        e.head.understructure === "bezel" ? ["Bezel wall", `${e.head.bezelWallMM.toFixed(2)} mm`] : null,
        e.sideStones.layout !== "one"
          ? [
              "Side stones",
              `${titleCase(e.sideStones.layout)} · ${titleCase(e.sideStones.shape)} · ` +
                `${Math.round(e.sideStones.caratRatio * 100)}% of centre`,
            ]
          : null,
        e.style ? ["Shank style", titleCase(e.style)] : null,
        e.cathedral ? ["Cathedral shoulders", "Yes"] : null,
        e.fit ? ["Fit", titleCase(e.fit)] : null,
        spec?.headVolumeMM3 !== undefined ? ["Head volume", `${spec.headVolumeMM3.toFixed(1)} mm³`] : null,
        // No engraving row here: the band half owns it (the "Engraving" block
        // above), and only `BandConfigurator.rebuildEngraving` draws it.
      ])
    : null;

  const weight = spec
    ? block("Weight", [
        ["Metal weight", `${spec.weightGrams.toFixed(2)} g`],
        ["Metal volume", `${spec.metalVolumeMM3.toFixed(1)} mm³`],
        ["Surface area", `${spec.surfaceAreaMM2.toFixed(0)} mm²`],
      ])
    : null;

  const quality = meleeQualityFactor(d);
  const money = spec
    ? block("Price (setting only)", [
        ["Metal", formatUSD(price.metalUSD)],
        price.paveUSD > 0 ? ["Melee stones", formatUSD(price.paveUSD)] : null,
        // Shown only when the specification actually moved the melee rate, so the
        // number above is explainable rather than mysterious.
        price.paveUSD > 0 && quality !== 1 ? ["Stone quality factor", `× ${quality.toFixed(2)}`] : null,
        ["Labour", formatUSD(price.laborUSD)],
        price.complexityUSD > 0 ? ["Setting complexity", formatUSD(price.complexityUSD)] : null,
        ["Retail margin", `× ${RETAIL_MARGIN}`],
        ["Estimated price", formatUSD(price.totalUSD)],
      ])
    : null;

  const warnings = spec?.warnings?.length
    ? block("Manufacturability", spec.warnings.map((w, i) => [`Note ${i + 1}`, w] as Row))
    : null;

  return [band, metal, stones, joints, engraving, engagement, weight, money, warnings].filter(
    (b): b is Block => b !== null,
  );
}

// ─── Print stylesheet ───────────────────────────────────────────────────────
//
// The sheet is portalled to <body>, so hiding the app is one rule. Everything
// inside prints black-on-white regardless of the viewer's theme.
const PRINT_CSS = `
@media print {
  html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
  body > *:not([data-spec-sheet]) { display: none !important; }
  [data-spec-sheet] { position: static !important; display: block !important; padding: 0 !important; background: #fff !important; }
  [data-spec-sheet] .spec-doc { max-width: none !important; max-height: none !important; overflow: visible !important;
    border: 0 !important; border-radius: 0 !important; box-shadow: none !important; background: #fff !important; }
  [data-spec-sheet] * { color: #000 !important; background: transparent !important; box-shadow: none !important; border-color: #bbb !important; }
  [data-spec-sheet] .spec-no-print { display: none !important; }
  [data-spec-sheet] section, [data-spec-sheet] tr { break-inside: avoid; page-break-inside: avoid; }
  @page { margin: 14mm; }
}`;

// ─── Component ──────────────────────────────────────────────────────────────

export interface SpecSheetRing {
  /** "Her Ring" / "His Ring". */
  label: string;
  state: RingState;
}

export interface SpecSheetProps {
  rings: SpecSheetRing[];
  catalogs: Catalogs;
  onClose: () => void;
  /** Second action, kept from the older panel: the raw configuration dump. */
  onDownloadJSON: () => void;
}

export function SpecSheet({ rings, catalogs, onClose, onDownloadJSON }: SpecSheetProps) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const grand = rings.reduce((sum, ring) => sum + estimateRingPrice(ring.state).totalUSD, 0);
  const priced = rings.some((ring) => ring.state.spec);

  return createPortal(
    <div
      data-spec-sheet
      data-testid="ring-spec-sheet-doc"
      role="dialog"
      aria-modal="true"
      aria-label="Specification sheet"
      onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-on-surface/40 p-6 backdrop-blur-sm"
    >
      <style>{PRINT_CSS}</style>

      <div className="spec-doc scroll-thin my-auto w-full max-w-[720px] rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-8 shadow-[0_18px_60px_rgba(0,0,0,0.18)]">
        <header className="mb-6 flex items-baseline justify-between gap-4 border-b border-outline-variant/40 pb-4">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-headline-md text-primary">Specification Sheet</h2>
            <p className="font-body text-[11px] text-outline">
              Ring Builder — generated {new Date().toLocaleDateString()}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="spec-no-print shrink-0 rounded-full p-1 text-on-surface-variant transition-colors hover:text-primary"
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        {rings.map((ring) => (
          <article key={ring.label} className="mb-6">
            <h3 className="mb-2 font-display text-[12px] font-semibold uppercase tracking-[0.16em] text-primary">
              {ring.label}
            </h3>
            {blocksFor(ring.state, catalogs).map((b) => (
              <section key={b.title} className="mb-3">
                <h4 className="mb-1 font-body text-[10px] uppercase tracking-[0.12em] text-outline">{b.title}</h4>
                <table className="w-full border-collapse">
                  <tbody>
                    {b.rows.map(([label, value]) => (
                      <tr key={label} className="border-t border-outline-variant/30">
                        <td className="w-[45%] py-1 pr-3 align-top font-body text-[11px] text-on-surface-variant">
                          {label}
                        </td>
                        <td className="py-1 align-top font-body text-[11px] tabular-nums text-on-surface">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </article>
        ))}

        <div className="flex items-baseline justify-between border-t border-outline-variant/60 pt-3">
          <div className="flex flex-col">
            <span className="font-display text-[12px] font-semibold uppercase tracking-[0.14em] text-primary">
              Grand total
            </span>
            <span className="font-body text-[10px] text-outline">
              {rings.length > 1 ? `${rings.length} rings · ` : ""}
              setting only — live estimate, not a quote. UK size is the nearest letter.
            </span>
          </div>
          <span
            data-testid="spec-sheet-grand-total"
            className="font-display text-headline-md leading-none text-primary"
          >
            {priced ? formatUSD(grand) : "—"}
          </span>
        </div>

        <div className="spec-no-print mt-6 flex flex-wrap gap-2">
          <Button variant="primary" onClick={() => window.print()}>
            <Icon name="print" size={15} />
            Print / PDF
          </Button>
          <Button variant="outline" onClick={onDownloadJSON}>
            <Icon name="download" size={15} />
            Download (.json)
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
